import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { productService } from '@/services';
import { toast } from 'sonner';

/**
 * Hook for managing wishlist items
 * This hook manages both the wishlist IDs and full product data
 */
export function useWishlist() {
  const queryClient = useQueryClient();

  // Get wishlist items - fetch full product data and extract IDs
  const { data: wishlistData, isLoading } = useQuery({
    queryKey: ['wishlist'],
    queryFn: async () => {
      const response = await productService.getWishlistProducts();
      
      // Extract product IDs from the full product objects
      const productIds = response.data.map((item: any) => {
        const product = item.product || item;
        return product._id || product.id;
      });
      
      return {
        success: response.success,
        data: productIds, // Store only IDs for efficient checking
        fullData: response.data, // Keep full data for wishlist page
      };
    },
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const wishlistProductIds = wishlistData?.data || [];

  // Add to wishlist mutation
  const addToWishlistMutation = useMutation({
    mutationFn: (productId: string) => productService.addToWishlist(productId),
    onMutate: async (productId) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['wishlist'] });

      // Snapshot the previous value
      const previousWishlist = queryClient.getQueryData(['wishlist']);

      // Optimistically update to the new value
      queryClient.setQueryData(['wishlist'], (old: any) => {
        if (!old) return { success: true, data: [productId], fullData: [] };
        
        // Only add if not already in wishlist
        if (old.data.includes(productId)) return old;
        
        return {
          ...old,
          data: [...old.data, productId],
        };
      });

      return { previousWishlist };
    },
    onSuccess: () => {
      toast.success('Added to wishlist');
      
      // Silently refetch in background to get full product data without showing loading
      queryClient.invalidateQueries({ 
        queryKey: ['wishlist'],
        refetchType: 'none' // Don't refetch immediately, just mark as stale
      });
      
      // Refetch after a short delay to get updated data
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['wishlist'] });
      }, 500);
    },
    onError: (error: any, productId, context) => {
      console.error('Failed to add to wishlist:', error);
      
      // Rollback to previous state on error
      if (context?.previousWishlist) {
        queryClient.setQueryData(['wishlist'], context.previousWishlist);
      }
      
      toast.error(error?.response?.data?.message || error?.message || 'Failed to add to wishlist');
      
      // Refetch to get correct state from server
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
    },
  });

  // Remove from wishlist mutation
  const removeFromWishlistMutation = useMutation({
    mutationFn: (productId: string) => productService.removeFromWishlist(productId),
    onMutate: async (productId) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['wishlist'] });

      // Snapshot the previous value
      const previousWishlist = queryClient.getQueryData(['wishlist']);

      // Optimistically update to the new value
      queryClient.setQueryData(['wishlist'], (old: any) => {
        if (!old) return { success: true, data: [], fullData: [] };
        
        return {
          ...old,
          data: old.data.filter((id: string) => id !== productId),
          fullData: old.fullData ? old.fullData.filter((item: any) => {
            const product = item.product || item;
            const id = product._id || product.id;
            return id !== productId;
          }) : [],
        };
      });

      return { previousWishlist };
    },
    onSuccess: () => {
      toast.success('Removed from wishlist');
      
      // Silently refetch in background
      queryClient.invalidateQueries({ 
        queryKey: ['wishlist'],
        refetchType: 'none'
      });
      
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['wishlist'] });
      }, 500);
    },
    onError: (error: any, productId, context) => {
      console.error('Failed to remove from wishlist:', error);
      
      // Rollback to previous state on error
      if (context?.previousWishlist) {
        queryClient.setQueryData(['wishlist'], context.previousWishlist);
      }
      
      toast.error(error?.response?.data?.message || error?.message || 'Failed to remove from wishlist');
      
      // Refetch to get correct state from server
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
    },
  });

  const isInWishlist = (productId: string) => {
    return wishlistProductIds.includes(productId);
  };

  const toggleWishlist = (productId: string) => {
    // Prevent multiple simultaneous operations
    if (addToWishlistMutation.isPending || removeFromWishlistMutation.isPending) {
      return;
    }

    const inWishlist = isInWishlist(productId);
    
    if (inWishlist) {
      // Product is in wishlist, so remove it
      removeFromWishlistMutation.mutate(productId);
    } else {
      // Product is not in wishlist, so add it
      addToWishlistMutation.mutate(productId);
    }
  };

  return {
    wishlistProductIds,
    wishlistFullData: wishlistData?.fullData || [],
    isLoading,
    isInWishlist,
    toggleWishlist,
    addToWishlist: (productId: string) => {
      // Only add if not already in wishlist
      if (!isInWishlist(productId)) {
        addToWishlistMutation.mutate(productId);
      }
    },
    removeFromWishlist: (productId: string) => {
      // Only remove if in wishlist
      if (isInWishlist(productId)) {
        removeFromWishlistMutation.mutate(productId);
      }
    },
    isAddingToWishlist: addToWishlistMutation.isPending,
    isRemovingFromWishlist: removeFromWishlistMutation.isPending,
  };
}